import { PartialLoadingFormData } from "../../../app/models/api/orderTransportation/partialLoadingFormData";
import { FullLoadingFormData } from "../../../app/models/api/orderTransportation/fullLoadingFormData";
import { LoadingTypeEnum } from "../../../app/enums/loadingTypeEnum";
import { supportedCurrencies } from "../../../app/constants/currencies";
import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import storageSession from "redux-persist/es/storage/session";
import { persistReducer } from "redux-persist";
import { RootState } from "../../../app/store";
import { cargoTypeDefaultValues } from "../../../app/constants/cargoTypeDefaultValues";
import { LogisticsAddress } from "../../../app/models/features/deliveryOptions/logisticsAddress";
import { LoadingAndReceivingAddress } from "../../../app/models/features/deliveryOptions/loadingAndReceivingAddress";
import { DeliveryInputResult } from "../../../app/models/features/deliveryOptions/deliveryInputResult";
import { LoadingInputResult } from "../../../app/models/features/loadingInput/loadingInputResult";
import { OrderResponse } from "../../../app/models/api/order/orderResponse";

interface LoadingInputForm {
    isSubmitted: boolean;
    loadingType: LoadingTypeEnum;
    partialLoadingFormData: PartialLoadingFormData;
    initialPartialLoadingFormData: PartialLoadingFormData;
    fullLoadingFormData: FullLoadingFormData;
    initialFullLoadingFormData: FullLoadingFormData;
}

type SliceState = {
    companyPayerId: string | null;
    confirmedProposalNumber: string | null;
    loadingInputForm: LoadingInputForm;
    deliveryFrom: LogisticsAddress;
    deliveryTo: LogisticsAddress;
    originalEditableOrder?: OrderResponse;
};

const defaultPartialLoadingFormData = {
    loadingInput: [Object.assign({}, cargoTypeDefaultValues[0])],
    additionalLoadingInput: {
        loadingWithTailLift: false,
        adrDangerousGoods: false,
        cargoDescription: "",
        costOfCargo: 0,
        costOfCargoCurrency: supportedCurrencies.eur,
    },
};

const defaultFullLoadingFormData: FullLoadingFormData = {
    bodyTypeId: null,
    carryingCapacity: null,
    loadingMetersLdm: null,
    loadingMethodId: null,
    additionalLoadingInput: {
        loadingWithTailLift: false,
        cargoDescription: "",
        adrDangerousGoods: false,
        costOfCargo: null,
        costOfCargoCurrency: supportedCurrencies.eur,
        shippingDate: null,
    },
};

const defaultLogisticsAddress = {
    countryCode: "",
    countryName: "",
    zip: "",
    city: "",
    address: "",
    companyName: "",
    contactPerson: "",
    email: "",
    phoneNumber: "",
    note: "",
    addToAddressBook: false,
} as LogisticsAddress;

const initialState: SliceState = {
    companyPayerId: null,
    confirmedProposalNumber: null,
    loadingInputForm: {
        isSubmitted: false,
        loadingType: LoadingTypeEnum.Partial,
        partialLoadingFormData: { ...defaultPartialLoadingFormData },
        initialPartialLoadingFormData: { ...defaultPartialLoadingFormData },
        fullLoadingFormData: { ...defaultFullLoadingFormData },
        initialFullLoadingFormData: { ...defaultFullLoadingFormData },
    },
    deliveryFrom: defaultLogisticsAddress,
    deliveryTo: defaultLogisticsAddress,
    originalEditableOrder: undefined,
};

const slice = createSlice({
    name: "orderFormSlice",
    initialState,
    reducers: {
        setPartialLoadingData: (state, { payload }: PayloadAction<PartialLoadingFormData>) => {
            state.loadingInputForm.isSubmitted = true;
            state.loadingInputForm.loadingType = LoadingTypeEnum.Partial;
            state.loadingInputForm.partialLoadingFormData = payload;
            state.loadingInputForm.fullLoadingFormData = { ...defaultFullLoadingFormData };
        },
        setFullLoadingData: (state, { payload }: PayloadAction<FullLoadingFormData>) => {
            state.loadingInputForm.isSubmitted = true;
            state.loadingInputForm.loadingType = LoadingTypeEnum.Full;
            state.loadingInputForm.fullLoadingFormData = payload;
            state.loadingInputForm.partialLoadingFormData = { ...defaultPartialLoadingFormData };
        },
        setLoadingAddress: (state, { payload }: PayloadAction<LogisticsAddress>) => {
            state.deliveryFrom = payload;
        },
        setReceivingAddress: (state, { payload }: PayloadAction<LogisticsAddress>) => {
            state.deliveryTo = payload;
        },
        setCompanyPayerId: (state, { payload }: PayloadAction<string>) => {
            state.companyPayerId = payload;
        },
        setDeliveryInputFrom: (state, { payload }: PayloadAction<DeliveryInputResult>) => {
            state.deliveryFrom.countryCode = payload.countryCode;
            state.deliveryFrom.countryName = payload.countryName;
            state.deliveryFrom.city = payload.city;
            state.deliveryFrom.zip = payload.zip;
        },
        setDeliveryInputTo: (state, { payload }: PayloadAction<DeliveryInputResult>) => {
            state.deliveryTo.countryCode = payload.countryCode;
            state.deliveryTo.countryName = payload.countryName;
            state.deliveryTo.city = payload.city;
            state.deliveryTo.zip = payload.zip;
        },
        setLoadingAndReceivingAddress: (state, { payload }: PayloadAction<LoadingAndReceivingAddress>) => {
            state.deliveryFrom = payload.loadingAddress;
            state.deliveryTo = payload.receivingAddress;
        },
        setOriginalEditableOrder: (state, { payload }: PayloadAction<OrderResponse | undefined>) => {
            state.originalEditableOrder = payload;
        },
        setConfirmedProposalNumber: (state, { payload }: PayloadAction<string | null>) => {
            state.confirmedProposalNumber = payload;
        },
        setOrderInitialState: () => initialState,
    },
});

export const {
    setPartialLoadingData,
    setFullLoadingData,
    setDeliveryInputFrom,
    setDeliveryInputTo,
    setLoadingAndReceivingAddress,
    setLoadingAddress,
    setReceivingAddress,
    setCompanyPayerId,
    setOriginalEditableOrder,
    setConfirmedProposalNumber,
    setOrderInitialState,
} = slice.actions;

export default slice.reducer;

const orderFormPersistConfig = {
    key: "orderForm",
    storage: storageSession,
};

export const orderFormPersistedReducer = persistReducer(orderFormPersistConfig, slice.reducer);

export const selectInitPartialLoadingFormData = (state: RootState) =>
    state.orderFormSlice.loadingInputForm.initialPartialLoadingFormData;

export const selectDeliveryFrom = (state: RootState) => state.orderFormSlice.deliveryFrom;

export const selectDeliveryTo = (state: RootState) => state.orderFormSlice.deliveryTo;

export const selectOrderFormSubmitted = (state: RootState) => state.orderFormSlice.loadingInputForm.isSubmitted;

export const selectPartialLoadingFormData = (state: RootState) =>
    state.orderFormSlice.loadingInputForm.partialLoadingFormData;

export const selectFullLoadingFormData = (state: RootState) =>
    state.orderFormSlice.loadingInputForm.fullLoadingFormData;

export const selectInitFullLoadingFormData = (state: RootState) =>
    state.orderFormSlice.loadingInputForm.initialFullLoadingFormData;

export const selectLoadingType = (state: RootState) =>
    state.orderFormSlice.loadingInputForm.loadingType;

export const selectCompanyPayerId = (state: RootState) => state.orderFormSlice.companyPayerId;

export const selectConfirmedProposalNumber = (state: RootState) => state.orderFormSlice.confirmedProposalNumber;

export const selectOriginalEditableOrder = (state: RootState) => state.orderFormSlice.originalEditableOrder;

export const selectDirectionCountryCodes = createSelector(
    (state: RootState) => state.orderFormSlice.deliveryFrom.countryCode,
    (state: RootState) => state.orderFormSlice.deliveryTo.countryCode,
    (departureCountryCode, destinationCountryCode) => ({
        departureCountryCode,
        destinationCountryCode,
    })
);

export const selectDirectionZips = createSelector(
    (state: RootState) => state.orderFormSlice.deliveryFrom.zip,
    (state: RootState) => state.orderFormSlice.deliveryTo.zip,
    (departureZip, destinationZip) => ({
        departureZip,
        destinationZip,
    })
);

export const selectLoadingAndReceivingAddress = createSelector(
    [(state) => state.orderFormSlice.deliveryFrom, (state) => state.orderFormSlice.deliveryTo],
    (loadingAddress, receivingAddress): LoadingAndReceivingAddress => ({
        loadingAddress,
        receivingAddress,
    })
);

export const selectLoadingInputResult = createSelector(
    [selectLoadingType, selectPartialLoadingFormData, selectFullLoadingFormData],
    (loadingType, partialLoadingFormData, fullLoadingFormData): LoadingInputResult =>
        loadingType === LoadingTypeEnum.Full
            ? { loadingType: LoadingTypeEnum.Full, data: fullLoadingFormData }
            : { loadingType: LoadingTypeEnum.Partial, data: partialLoadingFormData }
);
